import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';

/**
 * Kontenjan (allotment) yonetimi.
 *
 * Bir acentaya ayrilan oda serbest satistan duser: web siteniz, telefon,
 * kapidan giris ve diger acentalar o odayi satamaz. Etstur gibi bir kanalla
 * gercek zamanli baglanti olmadan cifte satisi engellemenin calisan yolu
 * budur — oda hic satista olmadigi icin ikinci kez satilamaz.
 *
 * "Serbest kalma" (release) suresi, girise X gun kala satilmayan kontenjani
 * kendiliginden geri alir; boylece oda bos kalmaz.
 */

interface Veri {
  baslangic: string;
  bitis: string;
  gunler: string[];
  acentalar: { id: string; unvan: string; kod: string }[];
  odaTipleri: { id: string; ad: string; odaSayisi: number }[];
  kontenjanlar: {
    agencyId: string;
    roomTypeId: string;
    tarih: string;
    kontenjan: number;
    kullanilan: number;
    releaseGun: number;
  }[];
  doluluk: { roomTypeId: string; tarih: string; dolu: number }[];
}

const isoGun = (d: Date) => d.toISOString().slice(0, 10);
const gunEkle = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoGun(d);
};
const kisaGun = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
const haftaGunu = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { weekday: 'short' });

export function Kontenjan({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('allotment:write');
  const [baslangic, setBaslangic] = React.useState(() => isoGun(new Date()));
  const [veri, setVeri] = React.useState<Veri | null>(null);
  const [seciliAcenta, setSeciliAcenta] = React.useState<string>('');
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [formAcik, setFormAcik] = React.useState(false);
  const [bilgi, setBilgi] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      const v = await api<Veri>(`/api/kanallar/kontenjan?baslangic=${baslangic}&gun=14`);
      setVeri(v);
      setSeciliAcenta((mevcut) => mevcut || (v.acentalar[0]?.id ?? ''));
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kontenjan yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, [baslangic]);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  const acenta = veri?.acentalar.find((a) => a.id === seciliAcenta) ?? null;

  const hucre = (roomTypeId: string, tarih: string) =>
    veri?.kontenjanlar.find(
      (k) => k.agencyId === seciliAcenta && k.roomTypeId === roomTypeId && k.tarih === tarih,
    ) ?? null;

  const doluBul = (roomTypeId: string, tarih: string) =>
    veri?.doluluk.find((d) => d.roomTypeId === roomTypeId && d.tarih === tarih)?.dolu ?? 0;

  if (yukleniyor || !veri) {
    return <div className="h-72 animate-pulse rounded-[20px] bg-white" />;
  }

  if (veri.acentalar.length === 0) {
    return (
      <div className="rounded-[20px] border border-cizgi bg-white px-6 py-12 text-center">
        <p className="text-[17px] font-semibold text-lacivert">Tanımlı acenta yok</p>
        <p className="mx-auto mt-2 max-w-[52ch] text-sm leading-relaxed text-gri">
          Kontenjan ayırabilmek için önce acenta kaydı gerekir. Acenta eklendiğinde bu ekrandan tarih bazında oda
          ayırabilir, ayırdığınız odaların serbest satıştan düşmesini sağlayabilirsiniz.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[18px] [animation:rise_.4s_both]">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={seciliAcenta}
          onChange={(e) => setSeciliAcenta(e.target.value)}
          className="rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-sm font-semibold text-lacivert outline-none focus:border-marka-600"
        >
          {veri.acentalar.map((a) => (
            <option key={a.id} value={a.id}>{a.unvan}</option>
          ))}
        </select>

        <div className="inline-flex items-center overflow-hidden rounded-xl border border-cizgi bg-white">
          <button onClick={() => setBaslangic(gunEkle(baslangic, -14))} className="px-3 py-2.5 text-gri hover:bg-zemin hover:text-lacivert" aria-label="Önceki dönem">‹</button>
          <span className="min-w-[150px] px-2 text-center text-[14px] font-semibold">
            {kisaGun(veri.baslangic)} – {kisaGun(veri.bitis)}
          </span>
          <button onClick={() => setBaslangic(gunEkle(baslangic, 14))} className="px-3 py-2.5 text-gri hover:bg-zemin hover:text-lacivert" aria-label="Sonraki dönem">›</button>
        </div>
        <button
          onClick={() => setBaslangic(isoGun(new Date()))}
          className="rounded-xl border border-cizgi bg-white px-4 py-2.5 text-sm font-semibold text-lacivert hover:border-lacivert"
        >
          Bugün
        </button>

        {yazabilir && (
          <button
            onClick={() => setFormAcik(true)}
            className="ml-auto flex items-center gap-2 rounded-xl bg-marka-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_-14px_rgba(31,79,216,.8)] transition hover:bg-marka-700"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Kontenjan tanımla
          </button>
        )}
      </div>

      {hata && (
        <p role="alert" className="rounded-2xl border border-kirmizi/30 bg-[#fdeceb] px-4 py-3 text-sm font-medium text-kirmizi">
          {hata}
        </p>
      )}
      {bilgi && (
        <p className="rounded-2xl bg-yesil-zemin px-4 py-3 text-sm font-medium text-yesil">{bilgi}</p>
      )}

      <div className="overflow-hidden rounded-[20px] border border-cizgi bg-white shadow-[0_30px_60px_-40px_rgba(11,21,48,.25)]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-cizgi bg-[#fbfcfe]">
                <th className="sticky left-0 z-10 bg-[#fbfcfe] px-5 py-3 text-left text-[11px] font-bold uppercase tracking-[.06em] text-gri">
                  Oda tipi
                </th>
                {veri.gunler.map((g) => (
                  <th key={g} className="border-l border-satir px-2 py-2 text-center">
                    <span className="block text-[10px] font-semibold uppercase tracking-[.04em] text-gri">
                      {haftaGunu(g)}
                    </span>
                    <span className="block text-[13px] font-bold text-lacivert">{kisaGun(g)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {veri.odaTipleri.map((t) => (
                <tr key={t.id} className="border-b border-satir">
                  <td className="sticky left-0 z-10 bg-white px-5 py-3">
                    <span className="block font-semibold text-lacivert">{t.ad}</span>
                    <span className="text-xs text-gri">{t.odaSayisi} oda</span>
                  </td>
                  {veri.gunler.map((g) => {
                    const k = hucre(t.id, g);
                    const dolu = doluBul(t.id, g);
                    const ayrilan = k?.kontenjan ?? 0;
                    const satilan = k?.kullanilan ?? 0;
                    const serbest = Math.max(0, t.odaSayisi - dolu - Math.max(0, ayrilan - satilan));
                    return (
                      <td
                        key={g}
                        className="border-l border-satir px-2 py-2.5 text-center align-middle"
                        title={`${t.ad} · ${kisaGun(g)}\nAyrılan: ${ayrilan} (satılan ${satilan})\nSerbest satışta: ${serbest}`}
                      >
                        {ayrilan > 0 ? (
                          <>
                            <span className="block text-[15px] font-bold tabular-nums text-lacivert">
                              {ayrilan - satilan}
                            </span>
                            <span className="block text-[10px] text-gri">
                              {satilan > 0 ? `${satilan} satıldı` : `${ayrilan} ayrıldı`}
                            </span>
                          </>
                        ) : (
                          <span className="block text-[13px] text-[#c9d3ea]">—</span>
                        )}
                        <span
                          className="mx-auto mt-1 block h-1 w-6 rounded-full"
                          style={{ background: serbest === 0 ? '#e5484d' : serbest <= 2 ? '#f2b632' : '#e6ebf7' }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-satir bg-[#fbfcfe] px-5 py-3.5 text-[12.5px] text-gri">
          <span>
            Büyük sayı: <strong className="text-lacivert">{acenta?.unvan ?? 'acenta'}</strong>'ın hâlâ satabileceği
            oda.
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1 w-6 rounded-full bg-[#e6ebf7]" /> serbest satışta yer var
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1 w-6 rounded-full bg-sari" /> serbest satışta 1–2 oda
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1 w-6 rounded-full bg-kirmizi" /> serbest satış kapalı
          </span>
        </div>
      </div>

      <p className="rounded-[16px] bg-mavi-zemin px-4 py-3.5 text-[13px] leading-relaxed text-lacivert">
        Buradan ayırdığınız odalar web sitenizden, telefondan, kapıdan ve diğer acentalardan{' '}
        <strong>satılamaz</strong>. Acenta satışını size geç bildirse bile çifte satış olmaz. Girişe “serbest kalma”
        günü kadar süre kala satılmayan kontenjan kendiliğinden serbest satışa döner.
      </p>

      {formAcik && veri && (
        <KontenjanFormu
          veri={veri}
          seciliAcenta={seciliAcenta}
          onKapat={() => setFormAcik(false)}
          onKaydedildi={async (gun) => {
            setFormAcik(false);
            setBilgi(`${gun} güne kontenjan yazıldı.`);
            setTimeout(() => setBilgi(null), 4000);
            await yukle();
          }}
        />
      )}
    </div>
  );
}

const GIRDI =
  'w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

const HAFTA = [
  [1, 'Pzt'],
  [2, 'Sal'],
  [3, 'Çar'],
  [4, 'Per'],
  [5, 'Cum'],
  [6, 'Cmt'],
  [0, 'Paz'],
] as const;

function KontenjanFormu({
  veri,
  seciliAcenta,
  onKapat,
  onKaydedildi,
}: {
  veri: Veri;
  seciliAcenta: string;
  onKapat: () => void;
  onKaydedildi: (gun: number) => void;
}) {
  const [agencyId, setAgencyId] = React.useState(seciliAcenta || veri.acentalar[0]!.id);
  const [roomTypeId, setRoomTypeId] = React.useState(veri.odaTipleri[0]?.id ?? '');
  const [baslangic, setBaslangic] = React.useState(veri.baslangic);
  const [bitis, setBitis] = React.useState(veri.bitis);
  const [kontenjan, setKontenjan] = React.useState(2);
  const [releaseGun, setReleaseGun] = React.useState(3);
  const [gunler, setGunler] = React.useState<number[]>([]);
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);

  const tip = veri.odaTipleri.find((t) => t.id === roomTypeId);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await api<{ gun: number }>('/api/kanallar/kontenjan', {
        method: 'PUT',
        body: JSON.stringify({ agencyId, roomTypeId, baslangic, bitis, kontenjan, releaseGun, gunler }),
      });
      onKaydedildi(cevap.gun);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Kontenjan kaydedilemedi.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]" onClick={onKapat} role="presentation">
      <div className="flex min-h-full items-center justify-center p-4">
        <form
          onSubmit={gonder}
          onClick={(e) => e.stopPropagation()}
          className="flex w-full max-w-[520px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          <div>
            <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">Kontenjan tanımla</h2>
            <p className="mt-0.5 text-[13px] leading-relaxed text-gri">
              Seçtiğiniz odalar bu tarihlerde yalnızca bu acentaya kalır; başka hiçbir kanaldan satılamaz.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-gri">Acenta</span>
              <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className={GIRDI}>
                {veri.acentalar.map((a) => (
                  <option key={a.id} value={a.id}>{a.unvan}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-gri">Oda tipi</span>
              <select value={roomTypeId} onChange={(e) => setRoomTypeId(e.target.value)} className={GIRDI}>
                {veri.odaTipleri.map((t) => (
                  <option key={t.id} value={t.id}>{t.ad} ({t.odaSayisi} oda)</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-gri">Başlangıç</span>
              <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} className={GIRDI} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-gri">Bitiş</span>
              <input type="date" value={bitis} min={baslangic} onChange={(e) => setBitis(e.target.value)} className={GIRDI} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between text-[12.5px] font-semibold text-gri">
                Ayrılan oda
                {tip && <span className="font-medium">en fazla {tip.odaSayisi}</span>}
              </span>
              <input
                type="number"
                min={0}
                max={tip?.odaSayisi ?? 100}
                value={kontenjan}
                onChange={(e) => setKontenjan(Math.max(0, Number(e.target.value) || 0))}
                className={GIRDI}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-gri">Serbest kalma (gün)</span>
              <input
                type="number"
                min={0}
                max={365}
                value={releaseGun}
                onChange={(e) => setReleaseGun(Math.max(0, Number(e.target.value) || 0))}
                className={GIRDI}
              />
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="flex items-baseline justify-between text-[12.5px] font-semibold text-gri">
              Günler <span className="font-medium">boş bırakırsanız her gün</span>
            </span>
            <div className="flex flex-wrap gap-1.5">
              {HAFTA.map(([n, etiket]) => {
                const secili = gunler.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setGunler((o) => (secili ? o.filter((x) => x !== n) : [...o, n]))}
                    className={
                      'rounded-full border-[1.5px] px-3 py-1.5 text-[13px] font-semibold transition ' +
                      (secili
                        ? 'border-marka-600 bg-marka-600 text-white'
                        : 'border-cizgi bg-white text-lacivert hover:border-mavi-acik')
                    }
                  >
                    {etiket}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="rounded-xl bg-zemin px-3.5 py-3 text-[12.5px] leading-relaxed text-gri">
            <strong className="text-lacivert">Serbest kalma:</strong> girişe {releaseGun} gün kala satılmamış
            kontenjan kendiliğinden serbest satışa döner, oda boş kalmaz. Acentanın zaten sattığı odalar bu işlemden
            etkilenmez.
          </p>

          {hata && <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={gonderiliyor || !roomTypeId}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-50"
            >
              {gonderiliyor && <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />}
              {gonderiliyor ? 'Kaydediliyor…' : 'Kontenjanı yaz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
